import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "./ConfirmDialog";
import { EMAIL_REGEX } from "./RecipientsField";
import {
  createRecipient,
  updateRecipient,
  deleteRecipient,
  getApiError,
} from "./emailApi";

const MAX_VISIBLE = 200;

const RecipientEditorDialog = ({
  open,
  recipient,
  currentSession,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(recipient);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail(recipient?.email || "");
      setName(recipient?.name || "");
      setError("");
    }
  }, [open, recipient]);

  const handleSave = async () => {
    const payload = { email: email.trim(), name: name.trim() };
    if (!EMAIL_REGEX.test(payload.email))
      return setError("Enter a valid email address.");

    try {
      setSaving(true);
      setError("");
      const saved = isEdit
        ? await updateRecipient(currentSession, recipient.id, payload)
        : await createRecipient(currentSession, payload);
      onSaved(saved);
    } catch (e) {
      setError(getApiError(e, "Failed to save recipient."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{isEdit ? "Edit recipient" : "Add recipient"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            label="Email"
            required
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Name (used for {{recipient_name}})"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const RecipientManagerDialog = ({
  open,
  onClose,
  recipients,
  loading,
  error,
  onRetry,
  currentSession,
  onSaved,
  onDeleted,
}) => {
  const [search, setSearch] = useState("");
  const [editor, setEditor] = useState({ open: false, recipient: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q),
    );
  }, [recipients, search]);

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError("");
      await deleteRecipient(currentSession, deleteTarget.id);
      onDeleted(deleteTarget.id);
      closeDelete();
    } catch (e) {
      setDeleteError(getApiError(e, "Failed to delete recipient."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Saved recipients</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress color="warning" />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : (
            <>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by email or name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              {filtered.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  {recipients.length === 0
                    ? "No saved recipients yet."
                    : "No matches."}
                </Typography>
              ) : (
                <List disablePadding>
                  {filtered.slice(0, MAX_VISIBLE).map((r) => (
                    <ListItem
                      key={r.id}
                      divider
                      secondaryAction={
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() =>
                                setEditor({ open: true, recipient: r })
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => setDeleteTarget(r)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      }
                    >
                      <ListItemText
                        primary={r.email}
                        secondary={r.name || "No name"}
                        slotProps={{ primary: { noWrap: true } }}
                        sx={{ pr: 10 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {filtered.length > MAX_VISIBLE && (
                <Typography variant="caption" color="text.secondary">
                  Showing first {MAX_VISIBLE} of {filtered.length}. Refine your
                  search.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setEditor({ open: true, recipient: null })}
          >
            Add recipient
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <RecipientEditorDialog
        open={editor.open}
        recipient={editor.recipient}
        currentSession={currentSession}
        onClose={() => setEditor({ open: false, recipient: null })}
        onSaved={(saved) => {
          onSaved(saved);
          setEditor({ open: false, recipient: null });
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete saved recipient?"
        message={`${deleteTarget?.email || ""} will be removed from the saved list for all admins. Past emails aren't affected.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
};

export default RecipientManagerDialog;
